import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerService, ImportListItemResponse, PaginatedApiResponse, PspService } from '../../../../api';
import { BaseTokenService } from '../../../core/services/base-token.service';
import { ENVIRONMENTER } from '../../../core/token/base-environmenter.token';
import { BaseApplication } from '../../../shared/enums/base-application.enum';
import { BaseFileDownloadService } from '../../../shared/services/base-file-download.service';
import { BaseDataManagementImportHistoryPage } from '../_interfaces/base-data-management-import-history.interface';

@Injectable({ providedIn: 'root' })
export class BaseDataManagementImportHistoryService {
  private readonly isGrowth: boolean;

  constructor(
    private readonly pspService: PspService,
    private readonly customerService: CustomerService,
    private readonly tokenService: BaseTokenService,
    private readonly fileDownloadService: BaseFileDownloadService,
    @Inject(ENVIRONMENTER) environment: any
  ) {
    this.isGrowth = environment?.application?.app === BaseApplication.growth;
  }

  // The org id from the JWT is the PSP id for growth, the customer id for customer apps.
  getImportHistory(page: number, size: number): Observable<BaseDataManagementImportHistoryPage> {
    const organizationId = this.getOrganizationId();
    if (organizationId === null) {
      return of({ data: [], pagination: { current: page, total: 0, size, itemsCount: 0 } });
    }

    const list$ = this.isGrowth
      ? this.pspService.getPspImportsList({ psp: organizationId, page, size })
      : this.customerService.getCustomerImportsList({ customer: organizationId, page, size });

    // The endpoint is paginated (`meta.page`) at runtime, but its generated type is the bare
    // `ListApiResponse` (the Swagger annotation omits `meta`) — cast to read the pagination block.
    return list$.pipe(
      map((response) => {
        const paginated = response as PaginatedApiResponse & { data?: ImportListItemResponse[] };

        return {
          data: (paginated.data ?? []) as ImportListItemResponse[],
          pagination: paginated.meta.page,
        };
      })
    );
  }

  // Downloads the stored import file as an attachment under its original filename. The uuid's
  // parent/child scope is guarded backend-side; the org id from the JWT is the psp/customer id.
  downloadFile(item: ImportListItemResponse): void {
    const organizationId = this.getOrganizationId();
    if (organizationId === null || !item.importId) {
      return;
    }

    const file$ = this.isGrowth
      ? this.pspService.getPspImportsFile({ psp: organizationId, import: item.importId })
      : this.customerService.getCustomerImportsFile({ customer: organizationId, import: item.importId });

    const fileName = item.originalFilename ?? 'download';
    const extension = fileName.split('.').pop()?.toLowerCase();

    file$.subscribe((blob) => this.fileDownloadService.triggerBrowserDownload(blob, fileName, extension));
  }

  private getOrganizationId(): number | null {
    const token = this.tokenService.getToken();

    return token ? this.tokenService.decodeToken(token).organizationId : null;
  }
}
